/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDeviceIntegrationCoverageRequest,
  GetDeviceIntegrationCoverageSecurity,
} from "../models/operations/getdeviceintegrationcoverage.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDeviceIntegrationCoverageQuery,
  DeviceIntegrationCoverageQueryData,
  prefetchDeviceIntegrationCoverage,
  queryKeyDeviceIntegrationCoverage,
} from "./deviceIntegrationCoverage.core.js";
export {
  buildDeviceIntegrationCoverageQuery,
  type DeviceIntegrationCoverageQueryData,
  prefetchDeviceIntegrationCoverage,
  queryKeyDeviceIntegrationCoverage,
};

export type DeviceIntegrationCoverageQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getCoverage deviceIntegrations
 *
 * @remarks
 * Summarize agent coverage across the org's connected MDM inventories, optionally scoped to one provider.
 */
export function useDeviceIntegrationCoverage(
  request?: GetDeviceIntegrationCoverageRequest | undefined,
  security?: GetDeviceIntegrationCoverageSecurity | undefined,
  options?: QueryHookOptions<
    DeviceIntegrationCoverageQueryData,
    DeviceIntegrationCoverageQueryError
  >,
): UseQueryResult<
  DeviceIntegrationCoverageQueryData,
  DeviceIntegrationCoverageQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDeviceIntegrationCoverageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getCoverage deviceIntegrations
 *
 * @remarks
 * Summarize agent coverage across the org's connected MDM inventories, optionally scoped to one provider.
 */
export function useDeviceIntegrationCoverageSuspense(
  request?: GetDeviceIntegrationCoverageRequest | undefined,
  security?: GetDeviceIntegrationCoverageSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DeviceIntegrationCoverageQueryData,
    DeviceIntegrationCoverageQueryError
  >,
): UseSuspenseQueryResult<
  DeviceIntegrationCoverageQueryData,
  DeviceIntegrationCoverageQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDeviceIntegrationCoverageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDeviceIntegrationCoverageData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DeviceIntegrationCoverageQueryData,
): DeviceIntegrationCoverageQueryData | undefined {
  const key = queryKeyDeviceIntegrationCoverage(...queryKeyBase);

  return client.setQueryData<DeviceIntegrationCoverageQueryData>(key, data);
}

export function invalidateDeviceIntegrationCoverage(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "getCoverage",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDeviceIntegrationCoverage(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "getCoverage"],
  });
}
